import { useCallback, useEffect, useRef, useState } from 'react';
import { listPins, reorderPins } from '../../extensions/bundled/documents/documentsActions';
import { recordsContext } from '../../extensions/bundled/documents/documentsStore';
import { listRecordMeta } from '../../records/recordsClient';

/**
 * When each pinned page was last edited (ms): the later of the page record
 * (title, place) and its body record. Metadata only — no document content is
 * read for the Pinned section's "4 d".
 */
async function editedAt(scope, ids) {
  if (!ids.length) return new Map();
  const ctx = recordsContext(scope);
  const want = new Set(ids.map(String));
  const [docs, bodies, blocks] = await Promise.all([
    listRecordMeta({ workspaceId: ctx.workspaceId, extensionId: 'documents', recordType: 'document', scopeId: null }),
    listRecordMeta({ workspaceId: ctx.workspaceId, extensionId: 'documents', recordType: 'docbody', scopeId: null }),
    // A body stored per block changes its blocks, not its docbody row, as it is
    // edited — read for the pinned pages only, not every block of the scope.
    Promise.all(
      [...want].map((id) => listRecordMeta({ workspaceId: ctx.workspaceId, extensionId: 'documents', recordType: 'docblock', scopeId: id }))
    ).then((lists) => lists.flat()),
  ]);
  const out = new Map();
  const note = (id, at) => {
    if (!want.has(id) || !at) return;
    out.set(id, Math.max(out.get(id) || 0, Number(at)));
  };
  for (const row of docs || []) note(String(row.id), row.updated_at);
  for (const row of bodies || []) note(String(row.id).replace(/::body$/, ''), row.updated_at);
  for (const row of blocks || []) if (row.parent_id != null) note(String(row.parent_id), row.updated_at);
  return out;
}

/**
 * The Pinned section's data (card 1.4): the pinned page ids of this tree in
 * the user's order (a deleted page, or one of another workspace, drops out
 * silently — `listPins` keeps only ids present in `nodes`), their edit times,
 * and `reorder` for a drag within the section.
 */
export default function usePins({ scope, nodes, enabled }) {
  const [ids, setIds] = useState([]);
  const [ages, setAges] = useState(() => new Map());
  const seq = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    try {
      const next = await listPins(scope, nodes);
      const times = await editedAt(scope, next).catch(() => new Map());
      if (mine !== seq.current) return;
      setIds(next);
      setAges(times);
    } catch {
      if (mine === seq.current) setIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scope.type, scope.teamId, scope.scopeId, nodes]);

  useEffect(() => {
    reload();
  }, [reload]);

  const reorder = useCallback(
    async (orderedIds) => {
      setIds(orderedIds);
      try {
        await reorderPins(orderedIds, scope);
      } finally {
        reload();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reload, scope.type, scope.teamId, scope.scopeId]
  );

  return { ids, pinnedSet: new Set(ids), ages, reload, reorder };
}
